"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, ShoppingCart } from "lucide-react";
import { cn, formatPrice } from "@/lib/utils";
import { auroraLineBgClass, resolveLineSlugForProduct } from "@/lib/catalog/line-family";
import { parseStringProduct } from "@/lib/catalog/shopify-catalog";
import {
  formatGaugePipeList,
  stringCountLabel,
  categoryFromInstrumentName,
} from "@/lib/catalog/gauge-format";
import { getDefaultPurchasableVariant } from "@/lib/products/variant-options";
import { AVAILABILITY_LABEL, productAvailability } from "@/lib/products/availability";
import { isPurchaseBlocked } from "@/lib/products/purchase-eligibility";
import { GaugeInfo } from "@/components/common/GaugeInfo";
import { ProductImagePlaceholder } from "./ProductImagePlaceholder";
import { ColorSwatch } from "./ColorSwatch";
import { QuickAddModal } from "./QuickAddModal";
import { configureLabel, type ConfigureLink } from "@/lib/products/configure-link";
import type { ShopifyProduct } from "@/types/shopify";

interface ProductCardProps {
  product: ShopifyProduct;
  /** Where "Customize" goes, and whether it really reproduces this set. */
  configureLink?: ConfigureLink;
}

function getColorOption(product: ShopifyProduct) {
  return product.options.find((o) => o.name.toLowerCase() === "color");
}

export function ProductCard({ product, configureLink }: ProductCardProps) {
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const meta = parseStringProduct(product);
  // One shared decision — the same one the product page, /api/cart/add and the
  // product structured data use.
  const purchaseBlocked = isPurchaseBlocked(meta);
  const category = categoryFromInstrumentName(meta.instrument);
  const gaugeList = meta.individualGauges
    ? formatGaugePipeList(meta.individualGauges, category)
    : "";

  const lineSlug = resolveLineSlugForProduct(product);
  const lineBgClass = auroraLineBgClass(lineSlug);

  const availability = productAvailability(product);
  const defaultVariant = getDefaultPurchasableVariant(product.variants.nodes);
  // Prefer the product's featured image (the packaging box) over a variant's
  // single-color string photo, so cards don't all show the Black variant.
  const image = product.images.nodes[0] ?? defaultVariant?.image ?? null;
  const price = defaultVariant?.price ?? product.priceRange.minVariantPrice;
  const compareAtPrice = defaultVariant?.compareAtPrice ?? null;
  const onSale =
    compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);

  // Color swatches — preview only, never interactive (selection happens in Quick Add).
  const maxSwatches = 6;
  const colors = getColorOption(product)?.values ?? [];
  const visibleColors = colors.slice(0, maxSwatches);
  const overflowCount = colors.length - visibleColors.length;

  return (
    <>
      {/* The card is an article, not a link. Quick Add and Customize are real
          buttons and must not be nested inside an anchor — the title link below
          is the card's single navigation target, stretched over the card with
          ::after so the whole surface stays clickable. */}
      <article
        className="group relative flex flex-col rounded-md overflow-hidden border transition-all duration-200 hover:border-[#3A3A3A] bg-[#141414] focus-within:border-[#E8C547]"
        style={{ borderColor: "#2A2A2A" }}
      >
        {/* Thin line-color strip keeps brand identity without a noisy full-card gradient */}
        {lineBgClass && (
          <span className={cn(lineBgClass, "relative block h-1 w-full flex-shrink-0")} aria-hidden />
        )}

        {/* Image area */}
        <div
          className="relative overflow-hidden aspect-square"
          style={{ backgroundColor: "#0D0D0D" }}
        >
          {image ? (
            <Image
              src={image.url}
              alt={image.altText ?? product.title}
              fill
              className="object-cover transition-all duration-300 group-hover:scale-105"
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            />
          ) : (
            <ProductImagePlaceholder />
          )}

          {onSale && (
            <div
              className="absolute top-2 left-2 px-2 py-0.5 text-xs font-bold rounded"
              style={{ backgroundColor: "#E84040", color: "white" }}
            >
              SALE
            </div>
          )}

          {/* Shopify's availableForSale=false means out of stock — never that
              the product is an upcoming release. */}
          {availability !== "in-stock" && (
            <div
              className="absolute top-2 right-2 px-2 py-0.5 text-xs font-bold rounded"
              style={{ backgroundColor: "#3A3A3A", color: "#9A9A9A" }}
            >
              {AVAILABILITY_LABEL[availability]}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex flex-col flex-1 p-4 gap-2">
          {/* Product-line badge */}
          {meta.productLine && (
            <span
              className="self-start px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full"
              style={{ backgroundColor: "#E8C54720", color: "#E8C547" }}
            >
              {meta.productLine}
            </span>
          )}

          {/* Clean title. The stretched link makes the whole card clickable
              while keeping exactly one anchor in the accessibility tree. */}
          <h3
            className="text-sm font-semibold leading-snug line-clamp-2"
            style={{ fontFamily: "var(--font-display)" }}
          >
            <Link
              href={`/products/${product.handle}`}
              className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
            >
              {product.title}
            </Link>
          </h3>

          {/* This record's handle and title describe different sets, so no gauge
              or string count is claimed for it. */}
          {purchaseBlocked && (
            <p className="text-[11px]" style={{ color: "#8A8A8A" }}>
              <Link href="/contact" className="relative z-10 underline">
                Specification under review. Contact us to confirm this set.
              </Link>
            </p>
          )}

          {/* Gauge label + string count (count is separate from the gauge numbers) */}
          {!purchaseBlocked && (meta.gaugeName || meta.stringCount > 0) && (
            <p className="text-xs" style={{ color: "#9A9A9A" }}>
              {meta.gaugeName && (
                <span className="font-semibold" style={{ color: "#E0E0E0" }}>
                  {meta.gaugeName} Gauge
                </span>
              )}
              {meta.stringCount > 0 ? `${meta.gaugeName ? " · " : ""}${stringCountLabel(meta.stringCount)}` : ""}
            </p>
          )}

          {/* Full gauge list — beginner-friendly, wound strings marked with w */}
          {gaugeList && (
            <p className="text-[11px] font-mono flex items-center gap-1.5 flex-wrap" style={{ color: "#9A9A9A" }}>
              <span>{gaugeList}</span>
              <GaugeInfo />
            </p>
          )}

          {/* Color preview */}
          {visibleColors.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {visibleColors.map((colorName) => (
                <ColorSwatch key={colorName} name={colorName} hexCode={null} size="sm" />
              ))}
              {overflowCount > 0 && (
                <span className="text-xs" style={{ color: "#8A8A8A" }}>
                  +{overflowCount}
                </span>
              )}
            </div>
          )}
          {colors.length > 0 && (
            <span className="sr-only">
              Available in {colors.length} color{colors.length === 1 ? "" : "s"}: {colors.join(", ")}
            </span>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-bold" style={{ color: "#E8C547" }}>
                {formatPrice(price.amount)}
              </span>
              {onSale && compareAtPrice && (
                <span className="text-xs line-through" style={{ color: "#8A8A8A" }}>
                  {formatPrice(compareAtPrice.amount)}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                if (purchaseBlocked || availability !== "in-stock") return;
                setQuickAddOpen(true);
              }}
              disabled={purchaseBlocked || availability !== "in-stock"}
              className="relative z-10 flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-sm transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
              style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
              title="Add this ready-made set to your cart"
              aria-label={`Quick add ${product.title}, the ready-made set`}
            >
              <ShoppingCart size={13} />
              Quick Add
            </button>
          </div>

          {!purchaseBlocked && configureLink && (
            <Link
              href={configureLink.href}
              className="relative z-10 inline-flex items-center gap-1.5 self-start text-left text-xs font-semibold transition-colors mt-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
              style={{ color: "#E8C547" }}
              title={
                configureLink.exact
                  ? "Open the configurator with this exact set loaded"
                  : "Open the configurator for this instrument"
              }
            >
              {configureLabel(configureLink)}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>
      </article>

      <QuickAddModal
        product={product}
        open={!purchaseBlocked && quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
      />
    </>
  );
}
